import { Popover, PopoverBackdrop, PopoverButton, PopoverPanel } from '@headlessui/react';
import { AnchorProps } from '@headlessui/react/dist/internal/floating';
import clsx from 'clsx';
import { Fragment, useEffect, useRef } from 'react';
import { ReactElement } from 'react-markdown/lib/react-markdown';

type PopoverOptions = {
  anchor?: AnchorProps | 'none';
  transition?: boolean;
  backdrop?:
    | {
        className?: string;
      }
    | boolean;
  panel?: {
    className?: string;
  };
};

interface Props {
  children: [ReactElement, (props: { open: boolean; close: () => void }) => JSX.Element];
  options?: PopoverOptions;
  className?: string;
  onClose?: () => void;
  onAfterCloseTransition?: () => void;
}

export function CallaboPopover({
  children,
  options,
  className,
  onClose,
  onAfterCloseTransition,
}: Props): JSX.Element {
  return (
    <Popover className={clsx('group/popover', className)}>
      {({ open, close }) => (
        <PopoverContents
          options={options}
          onClose={onClose}
          close={close}
          isOpen={open}
          onAfterCloseTransition={onAfterCloseTransition}
        >
          {children}
        </PopoverContents>
      )}
    </Popover>
  );
}

function PopoverContents({
  children,
  options,
  onClose,
  isOpen,
  onAfterCloseTransition,
  close,
}: Props & { isOpen: boolean; close: () => void }) {
  const focusRef = useRef<HTMLButtonElement>(null);
  const hasOpenedRef = useRef(isOpen);
  useEffect(() => {
    if (isOpen) {
      hasOpenedRef.current = true;
    }

    // 열렸던 적이 없으면 onClose 호출하지 않음
    if (!hasOpenedRef.current) {
      return;
    }

    if (!isOpen) {
      focusRef.current?.blur();
      onClose?.();
    }
  }, [isOpen, onClose]);

  return (
    <>
      <PopoverButton as={Fragment} ref={focusRef}>
        {children[0]}
      </PopoverButton>
      <PopoverPanel>
        {options?.backdrop && (
          <PopoverBackdrop
            className={clsx(
              'fixed inset-0 w-full h-full cursor-default z-10',
              typeof options.backdrop !== 'boolean' && options.backdrop.className
            )}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              close();
            }}
          />
        )}
      </PopoverPanel>
      <PopoverPanel
        transition={options?.transition ?? true}
        anchor={options?.anchor === 'none' ? undefined : options?.anchor ?? 'bottom'}
        className={clsx(
          'transition duration-200 ease-in-out data-[closed]:-translate-y-1 data-[closed]:opacity-0 shadow z-[101] min-w-[var(--button-width)]',
          options?.panel?.className
        )}
        onTransitionEnd={(e) => {
          if (!isOpen) {
            if (e.propertyName === 'opacity') {
              //opacity일 때 한번만 동작하도록 처리
              onAfterCloseTransition?.();
            }
          }
        }}
      >
        {children[1]}
      </PopoverPanel>
    </>
  );
}

export const ArrowDown = ({ className }: { className?: string }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      className={clsx(
        'shrink-0 transition-[transform] duration-100 ease-in-out group-data-[open]/popover:rotate-180 group-[closed]/popover:rotate-0',
        className
      )}
    >
      <path
        d="M15 7.5L10 12.5L5 7.5"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};
